import { useMemo } from 'react';

import { usePostJSON } from './request';

interface SyncResults {
  nClients?: number;
  nCallers?: number;
  nVisits?: number;
  nAutoAgents?: number;
}

export interface Integration {
  id: number;
  system: string;
  type: string;
  metadata: Record<string, unknown>;
  tz: string;
  isEnabled: boolean;
  enabledAt: string;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  nextSyncAt?: string;
  lastSyncResults?: SyncResults;
}

interface IntegrationResponse {
  success: boolean;
  integrations: Integration[];
}

/**
 * React hook to fetch integrations data
 * @returns Object containing data, error, loading state, and refetch function
 */
export function useGetIntegrations() {
  return usePostJSON<IntegrationResponse>(
    '/api/integration/data',
    {},
    []
  );
}

export function useActiveIntegrations() {
  const { data } = useGetIntegrations();
  const result = useMemo(() => {
    const aiByKey: Record<string, Integration> = {};
    if (data?.integrations && data.integrations.length > 0) {
      for (const integration of data.integrations) {
        if (integration.isEnabled) {
          aiByKey[integration.system] = integration;
        }
      }
    }
    return aiByKey;
  }, [data]);
  return result;
}

// Integrated systems

export interface SystemConfig {
  key: string;
  name: string;
  metadata: Array<{
    key: string;
    label: string;
    type: 'text' | 'boolean';
    isSensitive: boolean;
    description?: string;
  }>;
  utils?: {
    getClientLink?: (metadata: Record<string, string>, externalId: string) => string | null;
    getCallerLink?: (metadata: Record<string, string>, externalId: string) => string | null;
  }
}

export const SYSTEM_CONFIGS: Record<string, SystemConfig> = {
  axiscare: {
    key: 'axiscare',
    name: 'AxisCare',
    metadata: [
      { key: 'siteNumber', label: 'Site Number', type: 'text', isSensitive: false },
      { key: 'apiKey', label: 'API Key', type: 'text', isSensitive: true },
      { key: 'syncClients', label: 'Sync Clients', type: 'boolean', isSensitive: false, description: 'Automatically create client records for active AxisCare clients' },
      { key: 'autoAgent', label: 'Auto-Assign Agents', type: 'boolean', isSensitive: false, description: 'Automatically assign agents to new clients without agent history' }
    ],
    utils: {
      getClientLink: (metadata: Record<string, string>, externalId: string) => {
        const siteNumber = metadata.siteNumber;
        if (!siteNumber) {
          return null;
        }
        return `https://${siteNumber}.axiscare.com/?customers-profile.php&id=${externalId}`;
      },
      getCallerLink: (metadata: Record<string, string>, externalId: string) => {
        const siteNumber = metadata.siteNumber;
        if (!siteNumber) {
          return null;
        }
        return `https://${siteNumber}.axiscare.com/?caregivers-profile.php&id=${externalId}`;
      }
    }
  },
  quo: {
    key: 'quo',
    name: 'Quo',
    metadata: [
      { key: 'apiKey', label: 'API Key', type: 'text', isSensitive: true },
      { key: 'userId', label: 'User ID', type: 'text', isSensitive: false },
      { key: 'fromPhone', label: 'From Phone', type: 'text', isSensitive: false }
    ]
  },
  bubble: {
    key: 'bubble',
    name: 'Bubble',
    metadata: [
      { key: 'apiKey', label: 'API Key', type: 'text', isSensitive: true },
      { key: 'baseUrl', label: 'Base URL', type: 'text', isSensitive: false }
    ]
  }
};
